import Modal, { type ReactModalProps } from 'react-modal';
import React, { useCallback, useEffect, useRef } from 'react';
import { keyboard } from '../lib/shortcuts';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

/**
 * Props for {@link CustomModal}: every react-modal prop, plus the three this
 * wrapper adds/intercepts.
 */
interface CustomModalProps extends ReactModalProps {
  /** Callback invoked when the enter key is pressed. */
  onEnter?: () => void;
  /**
   * Disable scrolling the body while the modal is open (default `true`). When
   * `false`, the modal content handles body scroll itself.
   */
  disableBodyScroll?: boolean;
  /** Optional inner scroll container (forwarded by some consumers). */
  scrollContainer?: HTMLElement | null;
}

// Configure react-modal (https://github.com/reactjs/react-modal)

Modal.defaultStyles.content = {
  backgroundColor: '#F8FAFB',
  padding: 0,
  margin: 'auto',
  border: 'none',
  outline: 'none',
};

Object.assign(Modal.defaultStyles.overlay, {
  zIndex: 100,
  backgroundColor: 'rgba(74,86,102,0.75)',
  height: '100vh',
  overflow: 'scroll',
  display: 'flex',
});

Modal.setAppElement('#root');

/**
 * Wrap the Modal component of react-modal to add a 'onEnter' prop that accepts
 * a callback to call when the enter key is pressed.
 *
 * Disable body scroll: on iOS (and android?), 100vh includes the space that is
 * covered by the collapsing address bar, which means the body scrolls. While
 * the modal is open, we disable scrolling the body as it give strange
 * side-effects.
 *
 * props:
 * - onEnter: the callback to call when enter is pressed
 * - disableBodyScroll (default true): disable scrolling the body while the
 *   modal is open. Can be set to false to handle this in the modal content
 *   itself. This is sometimes necessary because the enable/disable calls need a
 *   reference to the element that still needs to scroll. In here, this is the
 *   modal overlay, but in some cases there is a scroll container inside the
 *   modal content.
 */
function CustomModal({ disableBodyScroll: disableScroll = true, scrollContainer, onEnter, ...rest }: CustomModalProps) {
  const { isOpen } = rest;

  // The react-modal overlay node, captured via the overlayRef callback. Was an
  // instance field (this.overlayRef) on the class.
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Body-scroll lock/unlock helpers. They read the *current* overlay node and
  // the *current* disableScroll flag, so they live in refs to stay stable while
  // always seeing fresh values (the class read this.props/this.overlayRef
  // directly each call).
  const disableScrollRef = useRef(disableScroll);
  disableScrollRef.current = disableScroll;

  const lockBodyScroll = useCallback(() => {
    if (disableScrollRef.current && overlayRef.current) {
      disableBodyScroll(overlayRef.current);
    }
  }, []);

  const unlockBodyScroll = useCallback(() => {
    if (disableScrollRef.current && overlayRef.current) {
      enableBodyScroll(overlayRef.current);
    }
  }, []);

  // Keep the latest onEnter in a ref so the keyboard listener (registered once)
  // always calls the current handler — mirrors the class reading this.props in
  // its bound handleEnter.
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;

  // componentDidMount / componentWillUnmount: register the enter listener and,
  // if we mount already open, lock the body scroll. Cleanup removes the
  // listener and, if still open, unlocks the body scroll.
  //
  // `isOpen` is intentionally read only on mount/unmount here (the class did
  // the same in cDM/cWU); open/close transitions while mounted are handled by
  // the separate effect below. We read it through a ref so the mount/unmount
  // effect can stay [] without a lint complaint about a stale capture.
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  useEffect(() => {
    const handleEnter = () => {
      if (onEnterRef.current) {
        onEnterRef.current();
      }
    };
    keyboard.addListener('enter', handleEnter);
    if (isOpenRef.current) {
      // We are already open on mount
      lockBodyScroll();
    }
    return () => {
      keyboard.removeListener('enter', handleEnter);
      if (isOpenRef.current) {
        // We are still open on unmount
        unlockBodyScroll();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate: lock on open, unlock on close.
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    const prevIsOpen = prevIsOpenRef.current;
    if (isOpen && !prevIsOpen) {
      lockBodyScroll();
    } else if (!isOpen && prevIsOpen) {
      unlockBodyScroll();
    }
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Capture the overlay node. The class also locked immediately here when open,
  // because cDM may run before react-modal hands us the overlay ref.
  const captureOverlayRef = (node: HTMLDivElement | null) => {
    overlayRef.current = node;
    if (isOpenRef.current) {
      lockBodyScroll();
    }
  };

  return (
    <Modal
      closeTimeoutMS={200}
      overlayRef={disableScroll ? captureOverlayRef : undefined}
      {...rest}
    />
  );
}

export default CustomModal;
